import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  FaCheckCircle,
  FaDrum,
  FaExclamationCircle,
  FaGuitar,
  FaMagic,
  FaMicrophone,
  FaMusic,
  FaPalette,
  FaPenNib,
  FaPhotoVideo,
  FaRunning,
  FaSave,
  FaStar,
  FaTheaterMasks,
  FaUserAlt,
  FaUserTie,
  FaVideo,
} from "react-icons/fa";
import { db } from "../firebase";
import "./allArtistJoiningReport.css";

const DEFAULT_TARGET = 100;

const DEFAULT_ARTIST_CATEGORIES = [
  "DJ",
  "BEATBOXER",
  "RAPPER",
  "MODEL",
  "BOUNCER",
  "WRITER",
  "VIDEOGRAPHER",
  "PHOTOGRAPHER",
  "MUSIC PRODUCER",
  "FITNESS",
  "INFLUENCER",
  "DRUMMER",
  "GUITARIST",
  "BEAUTICIAN",
  "SINGER",
  "ARTIST",
  "CHOREOGRAPHER",
  "DIRECTOR",
  "PRODUCER",
  "INSTRUMENTALIST",
  "ACTOR",
  "ACTRESS",
  "MAGICIAN",
];

const CATEGORY_ICONS = {
  actor: <FaTheaterMasks />,
  actress: <FaTheaterMasks />,
  artist: <FaPalette />,
  beatboxer: <FaMicrophone />,
  beautician: <FaStar />,
  bouncer: <FaUserTie />,
  choreographer: <FaRunning />,
  director: <FaVideo />,
  dj: <FaMusic />,
  drummer: <FaDrum />,
  fitness: <FaRunning />,
  guitarist: <FaGuitar />,
  influencer: <FaUserAlt />,
  instrumentalist: <FaGuitar />,
  magician: <FaMagic />,
  model: <FaStar />,
  "music producer": <FaMusic />,
  music_producer: <FaMusic />,
  photographer: <FaPhotoVideo />,
  producer: <FaVideo />,
  rapper: <FaMicrophone />,
  singer: <FaMicrophone />,
  videographer: <FaVideo />,
  writer: <FaPenNib />,
};

export default function AllArtistJoiningReport() {
  const [activeTab, setActiveTab] = useState("manage");
  const [artists, setArtists] = useState([]);
  const [targets, setTargets] = useState({});
  const [draftTargets, setDraftTargets] = useState({});
  const [savingId, setSavingId] = useState("");
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const admin = useMemo(() => getStoredAdmin(), []);

  useEffect(() => {
    const unsubArtists = onSnapshot(
      collection(db, "artists"),
      (snap) => {
        setArtists(snap.docs.map((item) => ({ id: item.id, ...(item.data() || {}) })));
        setLoading(false);
      },
      (error) => {
        setLoading(false);
        showToast(setToast, error?.message || "Unable to load artists.", "error");
      }
    );

    const unsubTargets = onSnapshot(
      collection(db, "artist_joining_targets"),
      (snap) => {
        const next = {};
        snap.docs.forEach((item) => {
          const data = item.data() || {};
          const id = data.categoryId || item.id;
          next[id] = { id, ...data };
        });
        setTargets(next);
        setDraftTargets((prev) => {
          const merged = { ...prev };
          Object.values(next).forEach((target) => {
            if (merged[target.categoryId] === undefined) {
              merged[target.categoryId] = String(Number(target.target || DEFAULT_TARGET));
            }
          });
          return merged;
        });
      },
      (error) => {
        showToast(setToast, error?.message || "Unable to load joining targets.", "error");
      }
    );

    return () => {
      unsubArtists();
      unsubTargets();
    };
  }, []);

  const categoryRows = useMemo(() => {
    const map = new Map();

    DEFAULT_ARTIST_CATEGORIES.forEach((name) => {
      const id = categoryIdFor(name);
      map.set(id, {
        categoryId: id,
        categoryName: titleCaseCategory(name),
        iconKey: id,
        source: "default",
      });
    });

    artists.forEach((artist) => {
      artistCategoriesFromDoc(artist).forEach((name) => {
        const id = categoryIdFor(name);
        if (!id) return;
        map.set(id, {
          ...(map.get(id) || {}),
          categoryId: id,
          categoryName: titleCaseCategory(name),
          iconKey: id,
          source: "artists",
        });
      });
    });

    Object.values(targets).forEach((target) => {
      const name = target.categoryName || target.categoryId;
      const id = target.categoryId || categoryIdFor(name);
      if (!id) return;
      map.set(id, {
        ...(map.get(id) || {}),
        categoryId: id,
        categoryName: titleCaseCategory(name),
        iconKey: categoryIdFor(name),
        source: "targets",
      });
    });

    const joinedCounts = joinedCountsByCategory(artists);

    return Array.from(map.values())
      .map((row) => {
        const targetDoc = targets[row.categoryId] || {};
        const target = positiveNumber(targetDoc.target, DEFAULT_TARGET);
        const joined = Number(joinedCounts.get(row.categoryId) || 0);
        const remaining = Math.max(0, target - joined);
        const progress = target > 0 ? Math.min(100, Math.round((joined / target) * 100)) : 0;

        return {
          ...row,
          icon: targetDoc.icon || iconLabelFor(row.categoryId),
          target,
          joined,
          remaining,
          progress,
          updatedAt: targetDoc.updatedAt || null,
        };
      })
      .sort((a, b) => a.categoryName.localeCompare(b.categoryName));
  }, [artists, targets]);

  async function saveTarget(category) {
    const rawValue = draftTargets[category.categoryId] ?? category.target;
    const targetValue = Number(rawValue);
    if (!Number.isFinite(targetValue) || targetValue <= 0) {
      showToast(setToast, "Target must be a positive number.", "error");
      return;
    }

    setSavingId(category.categoryId);
    try {
      const payload = {
        categoryId: category.categoryId,
        categoryName: category.categoryName,
        icon: category.icon,
        target: Math.round(targetValue),
        updatedAt: serverTimestamp(),
      };
      const updatedBy = admin?.email || admin?.mobile || admin?.name || admin?.docId || "";
      if (updatedBy) payload.updatedBy = updatedBy;

      await setDoc(doc(db, "artist_joining_targets", category.categoryId), payload, { merge: true });
      showToast(setToast, `${category.categoryName} target saved.`, "success");
    } catch (error) {
      showToast(setToast, error?.message || "Unable to save target.", "error");
    } finally {
      setSavingId("");
    }
  }

  return (
    <div className="ajr-page">
      <section className="ajr-hero">
        <div>
          <p className="ajr-kicker">Artist Growth Monitor</p>
          <h2>All Artist Joining Report</h2>
          <p>
            Track category-wise artist joining progress against campaign targets with live Firestore data.
          </p>
        </div>
        <div className="ajr-hero-metrics">
          <Metric label="Categories" value={categoryRows.length} />
          <Metric label="Artists Joined" value={artists.length} />
          <Metric label="Default Target" value={DEFAULT_TARGET} />
        </div>
      </section>

      <div className="ajr-tabs">
        <button
          type="button"
          className={activeTab === "manage" ? "is-active" : ""}
          onClick={() => setActiveTab("manage")}
        >
          Manage Report
        </button>
        <button
          type="button"
          className={activeTab === "report" ? "is-active" : ""}
          onClick={() => setActiveTab("report")}
        >
          Artist Joining Report
        </button>
      </div>

      {activeTab === "manage" ? (
        <section className="ajr-section">
          <div className="ajr-section-head">
            <div>
              <h3>Manage Report</h3>
              <p>Set the artist joining target for every discovered/default category.</p>
            </div>
          </div>

          <div className="ajr-card-grid">
            {categoryRows.map((category) => (
              <article className="ajr-category-card" key={category.categoryId}>
                <div className="ajr-card-title">{category.categoryName}</div>
                <div className="ajr-card-icon" aria-hidden="true">
                  {iconFor(category.categoryId)}
                </div>
                <label className="ajr-field">
                  <span>Target</span>
                  <input
                    type="number"
                    min="1"
                    value={draftTargets[category.categoryId] ?? category.target}
                    onChange={(event) =>
                      setDraftTargets((prev) => ({
                        ...prev,
                        [category.categoryId]: event.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  type="button"
                  className="ajr-save-btn"
                  onClick={() => saveTarget(category)}
                  disabled={savingId === category.categoryId}
                >
                  <FaSave /> {savingId === category.categoryId ? "Saving..." : "Update Target"}
                </button>
                <div className="ajr-muted">Last updated: {formatDate(category.updatedAt)}</div>
              </article>
            ))}
          </div>
        </section>
      ) : (
        <section className="ajr-section">
          <div className="ajr-section-head">
            <div>
              <h3>Artist Joining Report</h3>
              <p>
                Joined counts are calculated from real `artists` documents and update as Firestore changes.
              </p>
            </div>
            {loading ? <span className="ajr-live-pill">Loading...</span> : <span className="ajr-live-pill">Live</span>}
          </div>

          <div className="ajr-card-grid">
            {categoryRows.map((category) => (
              <article className="ajr-report-card" key={category.categoryId}>
                <div className="ajr-card-title">{category.categoryName}</div>
                <div className="ajr-card-icon" aria-hidden="true">
                  {iconFor(category.categoryId)}
                </div>
                <div className="ajr-report-lines">
                  <ReportLine label="Target" value={category.target} />
                  <ReportLine label="Joined" value={category.joined} />
                  <ReportLine label="Remaining" value={category.remaining} />
                </div>
                <div className="ajr-progress-wrap">
                  <div className="ajr-progress-copy">
                    <span>Progress</span>
                    <strong>{category.progress}%</strong>
                  </div>
                  <div className="ajr-progress-track">
                    <span style={{ width: `${category.progress}%` }} />
                  </div>
                </div>
                <div className="ajr-muted">Last updated: {formatDate(category.updatedAt)}</div>
              </article>
            ))}
          </div>
        </section>
      )}

      {toast ? (
        <div className={`ajr-toast ${toast.type === "error" ? "is-error" : "is-success"}`}>
          {toast.type === "error" ? <FaExclamationCircle /> : <FaCheckCircle />}
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="ajr-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function ReportLine({ label, value }) {
  return (
    <div className="ajr-report-line">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function artistCategoriesFromDoc(data = {}) {
  return normalizeStringArray(
    data.artistCategories ||
      data.categories ||
      data.category ||
      data.categoryName ||
      data.artistCategory ||
      data.artistType ||
      data.tags
  );
}

function joinedCountsByCategory(artists) {
  const counts = new Map();
  artists.forEach((artist) => {
    const categories = artistCategoriesFromDoc(artist);
    categories.forEach((category) => {
      const id = categoryIdFor(category);
      if (!id) return;
      counts.set(id, (counts.get(id) || 0) + 1);
    });
  });
  return counts;
}

function normalizeStringArray(value) {
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map((item) => cleanText(item)).filter(Boolean)));
  }

  const text = cleanText(value);
  if (!text) return [];

  return Array.from(
    new Set(
      text
        .split(/[\n,|/]+/)
        .map((item) => cleanText(item))
        .filter(Boolean)
    )
  );
}

function categoryIdFor(value) {
  return cleanText(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function titleCaseCategory(value) {
  const text = cleanText(value).replace(/_/g, " ");
  if (!text) return "Uncategorized";
  return text
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .replace(/\bDj\b/g, "DJ");
}

function cleanText(value) {
  return String(value ?? "").trim();
}

function positiveNumber(value, fallback) {
  const next = Number(value);
  return Number.isFinite(next) && next > 0 ? Math.round(next) : fallback;
}

function iconFor(categoryId) {
  return CATEGORY_ICONS[categoryId] || <FaStar />;
}

function iconLabelFor(categoryId) {
  return categoryId || "artist";
}

function formatDate(value) {
  const ms = toMillis(value);
  if (!ms) return "Not updated yet";
  try {
    return new Intl.DateTimeFormat("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}

function toMillis(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === "function") return value.toMillis();
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function getStoredAdmin() {
  try {
    return JSON.parse(localStorage.getItem("employee") || "{}");
  } catch {
    return {};
  }
}

function showToast(setToast, message, type = "success") {
  setToast({ message, type });
  window.setTimeout(() => setToast(null), 2800);
}
